import type { ReactNode } from 'react';
import { useCallback, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { ArrowLeft, History, Plus, Sparkles, Trash2 } from '@/shared/ui/icons';
import { Button } from '../../../shared/ui/button';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../../shared/ui/alert-dialog';
import { SIMessageComposer } from '../../si/SIMessageComposer';
import { TAKEOFF_MODELS, readStoredTakeoffModel, storeTakeoffModel, takeoffModelId } from '../ai/takeoffModels';
import { SIActivityStatus } from '../../si/SIActivityStatus';
import { useSIPanelState } from '../../si/useSIPanelState';
import { InspectorHeader } from '../../../shared/ui/inspector';
import { JumpToLatestButton } from '../../../shared/ui/jump-to-latest';
import { useJumpToLatestIndicator } from '../../../shared-ui/scroll/useJumpToLatestIndicator';
import { AIResponseMarkdown } from '../../../shared/ui/ai-response-markdown';
import type { TakeoffAiConversation, TakeoffAiMessage } from '../ai/useTakeoffComputer';
import { isGoalEvent, latestGoalState } from '../ai/takeoffGoalEvents';

interface Props {
  composerActions?: ReactNode;
  assistantName?: string;
  usageDrawing?: { projectId: string; sheetId: string; page: number };
  messages: TakeoffAiMessage[];
  conversations: TakeoffAiConversation[];
  conversationId: string | null;
  historyOpen: boolean;
  historyLoading: boolean;
  historyError: string | null;
  running: boolean;
  activity?: string | null;
  /** Steps the current run has completed, oldest first. */
  progress?: string[];
  streamingReply?: string;
  queueWhileRunning?: boolean;
  queuedMessages?: ReactNode;
  ready: boolean;
  onHistoryOpenChange: (open: boolean) => void;
  onReloadHistory: () => void;
  onSelectConversation: (id: string, initialLimit?: number) => void;
  onDeleteConversation: (id: string) => Promise<boolean>;
  onNewChat: () => void;
  onSend: (prompt: string) => Promise<boolean>;
  onSteer?: (prompt: string) => Promise<boolean>;
  onStop: () => void;
}

export function TakeoffAIInspector({ messages, conversations, conversationId, historyOpen, historyLoading,
  historyError, running, activity, progress = [], streamingReply = '', ready, onHistoryOpenChange, onReloadHistory, onSelectConversation, onNewChat,
  onDeleteConversation, onSend, onSteer, onStop, usageDrawing, assistantName = 'QuantisAI', composerActions,
  queueWhileRunning = false, queuedMessages }: Props) {
  const draftKey = `drawing-draft:${usageDrawing?.projectId ?? ''}:${usageDrawing?.sheetId ?? ''}:${usageDrawing?.page ?? ''}:${conversationId ?? 'new'}`;
  const [draft, setDraft] = useSIPanelState(draftKey, '');
  const [takeoffModel, setTakeoffModel] = useState(readStoredTakeoffModel);
  const chooseTakeoffModel = (value: string) => { const model = takeoffModelId(value); storeTakeoffModel(model); setTakeoffModel(model); };
  const draftRef = useRef(draft); draftRef.current = draft;
  const draftKeyRef = useRef(draftKey); draftKeyRef.current = draftKey;
  const [deleteTarget, setDeleteTarget] = useState<TakeoffAiConversation | null>(null);
  const [deleting, setDeleting] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const conversationRef = useRef<HTMLDivElement>(null);
  const historyListRef = useRef<HTMLDivElement>(null);
  const followLatestRef = useRef(true);
  const previousViewRef = useRef({ conversationId, historyOpen, running,
    firstMessageId: messages[0]?.id, messageCount: messages.length, scrollHeight: 0 });
  const latest = useJumpToLatestIndicator({ itemCount: messages.length, latestKey: messages.at(-1)?.id ?? messages.length, scope: conversationId ?? null });
  const { report: reportScroll, markAtBottom } = latest;
  const jumpToLatest = useCallback(() => {
    followLatestRef.current = true;
    markAtBottom();
    const conversation = conversationRef.current;
    if (!conversation) return;
    if (typeof conversation.scrollTo === 'function') conversation.scrollTo({ top: conversation.scrollHeight, behavior: 'smooth' });
    else conversation.scrollTop = conversation.scrollHeight;
  }, [markAtBottom]);
  const goalState = useMemo(() => latestGoalState(messages), [messages]);
  const completedGoals = goalState?.goals.filter(goal => goal.complete).length ?? 0;
  const reviewedGoals = goalState?.goals.filter(goal => goal.reviewed).length ?? 0;



  useLayoutEffect(() => {
    const previous = previousViewRef.current;
    if (previous.conversationId !== conversationId || (previous.historyOpen && !historyOpen)
      || (!previous.running && running)) followLatestRef.current = true;
    const conversation = conversationRef.current;
    if (!historyOpen && conversation) {
      if (followLatestRef.current) conversation.scrollTop = conversation.scrollHeight;
      else if (previous.conversationId === conversationId && messages.length > previous.messageCount
        && previous.firstMessageId && messages.findIndex(message => message.id === previous.firstMessageId) > 0)
        conversation.scrollTop += conversation.scrollHeight - previous.scrollHeight;
    }
    previousViewRef.current = { conversationId, historyOpen, running, firstMessageId: messages[0]?.id,
      messageCount: messages.length, scrollHeight: conversation?.scrollHeight ?? 0 };
  }, [messages, running, streamingReply, conversationId, historyOpen]);

  const send = async (steer = false) => {
    if (!draftRef.current.trim() || !ready || (running && !queueWhileRunning) || historyLoading) return;
    if (steer && (!running || !queueWhileRunning || !onSteer)) return;
    const submit = steer ? onSteer! : onSend;
    const prompt = draftRef.current;
    const sendingKey = draftKey;
    followLatestRef.current = true;
    if (conversationRef.current) conversationRef.current.scrollTop = conversationRef.current.scrollHeight;
    draftRef.current = '';
    setDraft('');
    const restore = () => {
      if (draftKeyRef.current === sendingKey) setDraft(current => {
        const restored = current || prompt;
        draftRef.current = restored;
        return restored;
      });
    };
    try {
      if (!await submit(prompt)) restore();
    } catch {
      restore();
    }
  };
  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      void send();
    }
  };
  return (
    <div className="flex h-full min-h-0 flex-col bg-background text-foreground" data-slot="takeoff-ai-inspector">
      <InspectorHeader title={historyOpen ? 'Chat history' : assistantName}
        leading={historyOpen ? <Button variant="ghost" size="icon" type="button" aria-label={'Back to ' + assistantName + ''}
          className="h-8 w-8" onClick={() => onHistoryOpenChange(false)}><ArrowLeft className="h-4 w-4" aria-hidden /></Button> : undefined}
        actions={<>
          {!historyOpen && <Button variant="ghost" size="icon" type="button" aria-label={'' + assistantName + ' history'}
            title="History" disabled={running} className="h-8 w-8" onClick={() => { onHistoryOpenChange(true); onReloadHistory(); }}>
            <History className="h-4 w-4" aria-hidden /></Button>}
          <Button variant="ghost" size="icon" type="button" aria-label={'New ' + assistantName + ' chat'}
            title="New chat" disabled={running} className="h-8 w-8" onClick={() => { setDraft(''); onNewChat(); }}>
            <Plus className="h-4 w-4" aria-hidden /></Button>
        </>} />
      {historyOpen ? <div ref={historyListRef} className="min-h-0 flex-1 overflow-y-auto px-2 py-2" aria-label={'' + assistantName + ' history'} aria-busy={historyLoading}>
        {historyError && <p className="px-2 py-3 text-xs text-destructive" role="alert">{historyError}</p>}
        {historyLoading && conversations.length === 0 && <div aria-hidden="true" className="space-y-3 p-3 motion-safe:animate-pulse">
          {[0, 1, 2].map(index => <div key={index} className="h-10 rounded-lg bg-muted" />)}
        </div>}
        {!historyLoading && conversations.length === 0 && !historyError &&
          <p className="px-2 py-3 text-sm text-muted-foreground">No chats on this drawing yet.</p>}
        {conversations.map(conversation => <div key={conversation.id}
          className={`group flex items-center rounded-lg hover:bg-muted ${conversation.id === conversationId ? 'bg-muted' : ''}`}>
          <button type="button" onClick={() => onSelectConversation(conversation.id,
            Math.max(8, Math.min(24, Math.ceil(((historyListRef.current?.clientHeight || 800) - 140) / 64))))}
            className="flex min-w-0 flex-1 flex-col gap-1 px-3 py-2.5 text-left">
            <span className="w-full truncate text-sm font-medium">{conversation.title}</span>
            <time className="text-xs text-muted-foreground" dateTime={conversation.updated_at}>
              {new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                .format(new Date(conversation.updated_at))}
            </time>
          </button>
          <Button variant="ghost" size="icon" type="button" className="mr-1 h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
            aria-label={`Delete chat: ${conversation.title}`} onClick={() => setDeleteTarget(conversation)}>
            <Trash2 className="h-4 w-4" aria-hidden />
          </Button>
        </div>)}
      </div> : <div className="relative flex min-h-0 flex-1 flex-col"><div ref={conversationRef} className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-4 [overflow-anchor:none]"
        role="log" aria-label={'' + assistantName + ' conversation'} aria-live="polite" aria-busy={historyLoading} onScroll={event => {
          const conversation = event.currentTarget;
          // Reading older messages pauses following until the user returns to
          // the bottom or explicitly starts another request/conversation.
          followLatestRef.current = conversation.scrollHeight - conversation.scrollTop - conversation.clientHeight <= 80;
          reportScroll({ offset: conversation.scrollTop, viewport: conversation.clientHeight, content: conversation.scrollHeight });
        }}>
        {historyError && <p className="text-xs text-destructive" role="alert">{historyError}</p>}
        {historyLoading && messages.length === 0 && <div aria-hidden="true" className="flex h-full flex-col justify-end gap-4 motion-safe:animate-pulse">
          <div className="ml-auto h-12 w-2/3 rounded-xl bg-muted" />
          <div className="space-y-2"><div className="h-3 w-full rounded bg-muted" /><div className="h-3 w-4/5 rounded bg-muted" /></div>
        </div>}
        {!historyLoading && !historyError && messages.length === 0 && <div className="flex h-full select-none flex-col items-center justify-center gap-2 text-center text-sm text-muted-foreground"><Sparkles className="h-5 w-5" aria-hidden /><p>Ask me to take off this drawing.</p></div>}
        {messages.map((message, index) => isGoalEvent(message.text) ? null : <div key={message.id ?? index} className={message.role === 'user'
          ? 'ml-5 min-w-0 max-w-full whitespace-pre-wrap rounded-xl bg-primary px-3 py-2 text-sm text-primary-foreground [overflow-wrap:anywhere]'
          : message.role === 'status' ? 'min-w-0 whitespace-pre-wrap text-xs text-muted-foreground [overflow-wrap:anywhere]' : 'min-w-0 max-w-full'}>
          {message.role === 'assistant' ? <AIResponseMarkdown text={message.text} /> : message.text}
        </div>)}
        {running && progress.length > 0 && <ol aria-label={`${assistantName} progress`} className="min-w-0 space-y-1 text-xs text-muted-foreground">
          {progress.slice(-10).map((step, index) => <li key={`${index}-${step}`} className="flex min-w-0 gap-1.5 [overflow-wrap:anywhere]">
            <span aria-hidden className="mt-[5px] h-1.5 w-1.5 shrink-0 rounded-full bg-primary/60" />{step}</li>)}
        </ol>}
        {running && (streamingReply ? <div aria-label={`${assistantName} reply in progress`} className="min-w-0 max-w-full">
          <AIResponseMarkdown text={streamingReply} />
        </div> : <SIActivityStatus activity={activity} />)}
      </div>
      {!latest.nearBottom && messages.length > 0 && <JumpToLatestButton unseen={latest.unseen} onClick={jumpToLatest} className="bottom-3" />}
      </div>}
      {!historyOpen && goalState && <details key={goalState.runId} open={running || undefined}
        className="mx-2 mb-1 shrink-0 rounded-lg border border-border bg-muted/40 text-xs" aria-label={'' + assistantName + ' goals'}>
        <summary className="cursor-pointer select-none px-3 py-2 font-medium">
          Goals: {completedGoals} of {goalState.goals.length} have saved work{reviewedGoals > 0 ? `, ${reviewedGoals} reviewed` : ''}
        </summary>
        <ul className="max-h-40 space-y-1 overflow-y-auto border-t border-border px-3 py-2">
          {goalState.goals.map(goal => <li key={goal.key} className="flex items-start gap-2">
            <span className={goal.complete ? 'text-primary' : 'text-muted-foreground'}>
              {goal.reviewed ? 'Reviewed' : goal.complete ? 'Saved work' : 'To do'}
            </span>
            <span className={goal.complete ? 'text-foreground' : 'text-muted-foreground'}>
              {goal.name}
              {(goal.view || goal.evidence) && <span className="block text-[11px] text-muted-foreground">
                {[goal.view, goal.evidence].filter(Boolean).join(' - ')}
              </span>}
            </span>
          </li>)}
          {!!goalState.excludedContext?.length && <li className="border-t border-border pt-1 text-muted-foreground">
            Not measured or unclear: {goalState.excludedContext.join('; ')}
          </li>}
        </ul>
      </details>}
      {!historyOpen && queuedMessages}
      {!historyOpen && <SIMessageComposer assistantName={assistantName} value={draft} onChange={value => { draftRef.current = value; setDraft(value); }}
        onSend={() => void send()} onSteer={onSteer ? () => void send(true) : undefined} onStop={onStop}
        running={running} queueWhileRunning={queueWhileRunning} disabled={!ready || (running && !queueWhileRunning) || historyLoading}
        inputRef={inputRef} onKeyDown={onKeyDown} actions={composerActions} takeoffModelPicker={{ value: takeoffModel, options: TAKEOFF_MODELS, onChange: chooseTakeoffModel }} sendAriaLabel={`Send message to ${assistantName}`} composerAriaLabel={`${assistantName} composer`} />}

      <AlertDialog open={!!deleteTarget} onOpenChange={open => { if (!open && !deleting) setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this chat?</AlertDialogTitle>
            <AlertDialogDescription>The conversation will be removed. Takeoff Types and measurements will remain on the drawing.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction disabled={deleting} className="bg-destructive text-white hover:bg-destructive/90"
              onClick={event => { event.preventDefault(); if (!deleteTarget) return; setDeleting(true);
                void onDeleteConversation(deleteTarget.id).then(deleted => { if (deleted) setDeleteTarget(null); }).finally(() => setDeleting(false)); }}>
              {deleting ? 'Deleting...' : 'Delete chat'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
